<template>
  <div
    class="h-full w-full flex flex-col items-center justify-between grow overflow-y-hidden"
  >
    <div class="w-full flex flex-col-reverse grow overflow-y-auto">
      <Feed
        variant="comments"
        :items="feed"
        :reverse="true"
        :task="task"
        class="grow overflow-y-auto"
        @preview="handleViewMedia"
      />
      <span
        v-if="hasNextPage && !isFetching"
        ref="loadMore"
        class="relative w-full"
      />
    </div>
    <CommentBox
      v-if="!readonly"
      ref="commentBox"
      :task="task"
      :mentionables="users"
      class="w-full"
    />
    <MediaViewer
      v-if="media.length"
      v-model:open="openMediaViewer"
      :title="activeMedia.name"
      :src="activeMedia?.urls?.file || activeMedia?.url"
      :type="activeMedia?.mime_type"
      :multiple="media.length > 1"
      @next="next"
      @previous="previous"
    />
  </div>
</template>

<script setup>
import { computed, onUnmounted, ref } from "vue";
import { useIntersectionObserver } from "@vueuse/core";
import { CommentBox } from "@/SharedComponents/CommentBox";
import { Feed } from "@/SharedComponents/Feed";
import { MediaViewer } from "@/SharedComponents/Media";
import { useActivityHistory } from "@/Composables/useActivityHistory";

const loadMore = ref(null);
const commentBox = ref(null);

const props = defineProps({
  task: { type: Object, required: true },
  feed: { type: Array, default: () => [] },
  users: { type: Array, default: () => [] },
  media: { type: Array, default: () => [] },
  readonly: { type: Boolean, default: false },
});

const activeIndex = ref(0);
const openMediaViewer = ref(undefined);

const activeMedia = computed(() => props.media[activeIndex.value]);

function handleViewMedia(file) {
  activeIndex.value = props.media.findIndex((media) => media.id === file.id);
  openMediaViewer.value = true;
}

function next() {
  activeIndex.value -= 1;
  if (activeIndex.value < 0) {
    activeIndex.value = props.media.length - 1;
  }
}

function previous() {
  activeIndex.value += 1;
  if (activeIndex.value === props.media.length) {
    activeIndex.value = 0;
  }
}

const { fetchNextPage, hasNextPage, isFetching } = useActivityHistory(
  props.task.tenant.id,
  props.task
);

const { stop } = useIntersectionObserver(loadMore, ([{ isIntersecting }]) => {
  if (isIntersecting && !isFetching.value) {
    fetchNextPage.value();
  }
});

onUnmounted(() => {
  stop();
});
</script>
